import React, { useState, useEffect } from 'react';
import { vehicleDetailAPI, vehicleRegistrationAPI } from '../services/api';
import VehicleDetailForm from './VehicleDetailForm';
import SearchableSelect from './SearchableSelect';
import Swal from 'sweetalert2';
import './VehicleDetailManagement.css';

const VehicleDetailManagement = ({ user }) => {
  const [vehicles, setVehicles] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  
  // Filters
  const [search, setSearch] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [hasInfo, setHasInfo] = useState('');
  const [unitCompletionStatus, setUnitCompletionStatus] = useState(''); // '', 'completed', 'incomplete'
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  
  // Modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importUnit, setImportUnit] = useState(''); // Separate state for import modal

  useEffect(() => {
    fetchUnits();
    fetchStatistics();
  }, [unitCompletionStatus]);

  useEffect(() => {
    fetchVehicles();
  }, [page, selectedUnit, hasInfo, units]); // Thêm units vào dependency

  // Real-time search with debounce
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (search !== undefined) {
        setPage(1);
        fetchVehicles();
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(delaySearch);
  }, [search]);

  const fetchUnits = async () => {
    try {
      if (user.role === 'admin' || user.role === 'supervisor') {
        // Lấy danh sách đơn vị với trạng thái hoàn thành
        const params = {};
        if (unitCompletionStatus) {
          params.completionStatus = unitCompletionStatus;
        }
        
        const response = await vehicleDetailAPI.getUnitsWithCompletionStatus(params);
        setUnits(response.data.units);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const params = {};
      if (selectedUnit) params.unitId = selectedUnit;
      
      const response = await vehicleDetailAPI.getVehicleStatistics(params);
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 20,
        search: search || undefined,
        hasInfo: hasInfo || undefined
      };

      // Nếu có chọn đơn vị cụ thể
      if (selectedUnit) {
        params.unitId = selectedUnit;
      } 
      // Nếu có filter trạng thái đơn vị (completed/incomplete) nhưng chưa chọn đơn vị cụ thể
      // thì lấy tất cả phương tiện của các đơn vị đã lọc
      else if (unitCompletionStatus && units.length > 0) {
        // Lấy danh sách unitIds từ units đã được filter
        const unitIds = units.map(u => u.id).join(',');
        params.unitIds = unitIds; // Gửi danh sách unitIds
      }

      const response = await vehicleDetailAPI.getVehicleDetails(params);
      setVehicles(response.data.vehicles);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchVehicles();
  };

  const handleImport = async () => {
    if (!importFile) {
      Swal.fire({
        icon: 'warning',
        title: 'Thiếu thông tin',
        text: 'Vui lòng chọn file Excel',
        confirmButtonColor: '#1e3a8a'
      });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', importFile);
      
      if (user.role === 'admin' && importUnit) {
        formData.append('unitId', importUnit);
      }

      const response = await vehicleDetailAPI.importLicensePlates(formData);
      setImportResult(response.data);
      setImportFile(null);
      fetchVehicles();
      fetchStatistics();
      
      Swal.fire({
        icon: 'success',
        title: 'Thành công!',
        text: 'Import dữ liệu thành công',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error importing:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: error.response?.data?.message || 'Lỗi khi import file',
        confirmButtonColor: '#1e3a8a'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (vehicle) => {
    try {
      const response = await vehicleDetailAPI.getVehicleDetailById(vehicle.id);
      setSelectedVehicle(response.data.vehicle);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching vehicle detail:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: 'Không thể tải thông tin phương tiện',
        confirmButtonColor: '#1e3a8a'
      });
    }
  };

  const handleSaveDetail = async (data) => {
    try {
      // Cập nhật thông tin phương tiện (upload ảnh đã được xử lý trong form)
      await vehicleDetailAPI.updateVehicleDetail(selectedVehicle.id, data);
      
      setShowDetailModal(false);
      setSelectedVehicle(null);
      fetchVehicles();
      fetchStatistics();
      
      Swal.fire({
        icon: 'success',
        title: 'Thành công!',
        text: 'Lưu thông tin thành công',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error updating vehicle:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: error.response?.data?.message || 'Lỗi khi cập nhật',
        confirmButtonColor: '#1e3a8a'
      });
    }
  };

  const handleResetDetail = async (vehicleId) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Xác nhận làm mới',
      html: 'Bạn có chắc muốn xóa tất cả thông tin chi tiết của phương tiện này?<br><small>Phương tiện sẽ chuyển về trạng thái "Chưa nhập"</small>',
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Làm mới',
      cancelButtonText: 'Hủy'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      // Gửi dữ liệu rỗng để reset
      const emptyData = {
        ownerName: null,
        ownerBirthYear: null,
        ownerPhone: null,
        ownerAddress: null,
        currentUserName: null,
        currentUserBirthYear: null,
        currentUserPhone: null,
        currentUserAddress: null,
        driverName: null,
        driverBirthYear: null,
        driverLicenseClass: null,
        driverLicenseNumber: null,
        driverPhone: null,
        driverAddress: null,
        isModified: false,
        isExtended: false,
        plateColor: null,
        platePosition: null,
        hasModifiedLights: false,
        usageExpiry: null,
        hasMonitoringDevice: false,
        monitoringAccount: null,
        monitoringPassword: null,
        monitoringProvider: null,
        collectorName: null,
        collectorUnit: null,
        hasPhoto: false,
        notes: null
      };

      await vehicleDetailAPI.updateVehicleDetail(vehicleId, emptyData);
      setShowDetailModal(false);
      setSelectedVehicle(null);
      fetchVehicles();
      fetchStatistics();
      
      Swal.fire({
        icon: 'success',
        title: 'Thành công!',
        text: 'Đã làm mới thông tin phương tiện',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error resetting vehicle:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: error.response?.data?.message || 'Lỗi khi làm mới',
        confirmButtonColor: '#1e3a8a'
      });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Xác nhận xóa',
      text: 'Bạn có chắc muốn xóa phương tiện này?',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Xóa',
      cancelButtonText: 'Hủy'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await vehicleDetailAPI.deleteVehicleDetail(id);
      fetchVehicles();
      fetchStatistics();
      
      Swal.fire({
        icon: 'success',
        title: 'Thành công!',
        text: 'Xóa phương tiện thành công',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: error.response?.data?.message || 'Lỗi khi xóa',
        confirmButtonColor: '#1e3a8a'
      });
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      
      Swal.fire({
        title: 'Đang xuất Excel...',
        html: `
          <div style="text-align: left; padding: 10px;">
            <p><strong>Backend đang xử lý dữ liệu và ảnh...</strong></p>
            <p style="color: #666; font-size: 14px; margin-top: 10px;">
              ⏱️ Quá trình này có thể mất <strong>5-15 phút</strong> tùy thuộc vào số lượng phương tiện và ảnh.
            </p>
            <p style="color: #666; font-size: 14px;">
              ⚠️ Vui lòng <strong>không đóng tab</strong> này trong khi đang xuất.
            </p>
            <p style="color: #999; font-size: 12px; margin-top: 15px;">
              Bạn có thể mở tab khác để làm việc trong lúc chờ đợi.
            </p>
          </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      // Xác định hasInfo dựa trên filter trạng thái đơn vị
      let exportHasInfo = 'true'; // Mặc định: xuất đã nhập thông tin
      
      if (unitCompletionStatus === 'incomplete') {
        // Nếu chọn "Đơn vị chưa hoàn thành" → xuất phương tiện chưa nhập
        exportHasInfo = 'false';
      }
      
      // Chuẩn bị params cho API
      const exportParams = {
        hasInfo: exportHasInfo
      };
      
      // Thêm filter đơn vị nếu có chọn đơn vị cụ thể
      if (selectedUnit) {
        exportParams.unitId = selectedUnit;
      }
      // Hoặc nếu có filter trạng thái đơn vị (completed/incomplete)
      else if (unitCompletionStatus && units.length > 0) {
        const unitIds = units.map(u => u.id).join(',');
        exportParams.unitIds = unitIds;
      }
      
      // Gọi endpoint mới - backend xử lý tất cả
      const response = await vehicleDetailAPI.exportExcelWithPhotos(exportParams);
      
      // Tạo filename
      const now = new Date();
      const statusSuffix = unitCompletionStatus === 'incomplete' ? 'chua-nhap' : 'da-nhap';
      const unitName = selectedUnit 
        ? units.find(u => u.id === parseInt(selectedUnit))?.name || 'don-vi'
        : unitCompletionStatus ? `${statusSuffix}` : 'tat-ca';
      const fileName = `thong-ke-phuong-tien-${unitName.toLowerCase().replace(/\s+/g, '-')}-${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}.xlsx`;

      // Download file
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);

      Swal.close();
      
      const statusText = unitCompletionStatus === 'incomplete' ? 'chưa nhập thông tin' : 'đã nhập thông tin';
      Swal.fire({
        icon: 'success',
        title: 'Thành công!',
        text: `Đã xuất Excel ${statusText}${selectedUnit ? ' của đơn vị đã chọn' : ''}`,
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: error.response?.data?.message || 'Lỗi khi xuất Excel: ' + error.message,
        confirmButtonColor: '#1e3a8a'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vehicle-detail-management">
      <h2>Quản lý chi tiết phương tiện</h2>

      {/* Statistics */}
      {statistics && (
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-label">Tổng số phương tiện</div>
            <div className="stat-value">{statistics.total}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Đã nhập thông tin</div>
            <div className="stat-value success">{statistics.withInfo}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Chưa nhập thông tin</div>
            <div className="stat-value warning">{statistics.withoutInfo}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Tỷ lệ hoàn thành</div>
            <div className="stat-value">{statistics.percentage}%</div>
          </div>
        </div>
      )}

      {/* Filters & Action Bar */}
      <div className="filters-section">
        <div className="filters-controls-wrap">
          <div className="filter-item search-item">
            <input
              type="text"
              placeholder="🔍 Tìm kiếm biển số..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="filter-input"
            />
          </div>

          {(user.role === 'admin' || user.role === 'supervisor') && (
            <>
              <div className="filter-item unit-status-item">
                <select 
                  value={unitCompletionStatus} 
                  onChange={(e) => {
                    setUnitCompletionStatus(e.target.value);
                    setSelectedUnit(''); // Reset selected unit khi đổi filter
                  }}
                  className="filter-select"
                >
                  <option value="">🏢 Tất cả đơn vị</option>
                  <option value="completed">✅ Đơn vị đã hoàn thành</option>
                  <option value="incomplete">⏳ Đơn vị chưa hoàn thành</option>
                </select>
              </div>

              {units.length > 0 && (
                <div className="filter-item unit-select-item">
                  <SearchableSelect
                    options={[
                      { value: '', label: '📍 Chọn đơn vị cụ thể' },
                      ...units.map(unit => ({ 
                        value: unit.id, 
                        label: `${unit.name} (${unit.withInfo}/${unit.total} - ${unit.percentage}%)`
                      }))
                    ]}
                    value={selectedUnit}
                    onChange={setSelectedUnit}
                    placeholder="📍 Chọn đơn vị cụ thể"
                  />
                </div>
              )}
            </>
          )}

          <div className="filter-item status-item">
            <select 
              value={hasInfo} 
              onChange={(e) => setHasInfo(e.target.value)}
              className="filter-select"
            >
              <option value="">📊 Tất cả trạng thái</option>
              <option value="true">✅ Đã nhập thông tin</option>
              <option value="false">⏳ Chưa nhập thông tin</option>
            </select>
          </div>
        </div>

        <div className="action-buttons">
          <button className="btn-action btn-import" onClick={() => setShowImportModal(true)} title="Nhập danh sách từ file Excel">
            <span className="btn-icon">📥</span>
            <span className="btn-text">Import Excel</span>
          </button>
          <button className="btn-action btn-export" onClick={handleExportExcel} disabled={loading} title="Xuất toàn bộ báo cáo chi tiết ra file Excel">
            <span className="btn-icon">📊</span>
            <span className="btn-text">Xuất Excel</span>
          </button>
        </div>
      </div>

      {/* Vehicle List */}
      {loading ? (
        <div className="loading">Đang tải dữ liệu...</div>
      ) : (
        <>
          <div className="vehicle-table">
            <table>
              <thead>
                <tr>
                  <th>STT</th>
                  <th>Biển số</th>
                  <th>Đơn vị</th>
                  <th>Chủ sở hữu</th>
                  <th>Người điều khiển</th>
                  <th>Trạng thái</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-data">Không có dữ liệu</td>
                  </tr>
                ) : (
                  vehicles.map((vehicle, index) => (
                    <tr key={vehicle.id}>
                      <td>{(page - 1) * 20 + index + 1}</td>
                      <td className="license-plate">{vehicle.licensePlate}</td>
                      <td>{vehicle.unit?.name}</td>
                      <td>{vehicle.ownerName || '-'}</td>
                      <td>{vehicle.driverName || '-'}</td>
                      <td>
                        <span className={`status-badge ${vehicle.ownerName ? 'complete' : 'incomplete'}`}>
                          {vehicle.ownerName ? 'Đã nhập' : 'Chưa nhập'}
                        </span>
                      </td>
                      <td className="actions">
                        <button
                          className="btn-edit"
                          onClick={() => handleViewDetail(vehicle)}
                          title="Nhập/Sửa thông tin"
                        >
                          ✏️
                        </button>
                        {user.role === 'admin' && (
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(vehicle.id)}
                            title="Xóa"
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                ← Trước
              </button>
              <span>Trang {page} / {pagination.totalPages}</span>
              <button
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Sau →
              </button>
            </div>
          )}
        </>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Import biển số từ Excel</h3>
            
            <div className="import-instructions">
              <p>File Excel cần có cột: <strong>BIENSO_DAYDU</strong></p>
              <p>Ví dụ: 29A-12345, 30H-67890</p>
            </div>

            {user.role === 'admin' && (
              <div className="form-group">
                <label>Chọn đơn vị:</label>
                <SearchableSelect
                  options={[
                    { value: '', label: '-- Chọn đơn vị --' },
                    ...units.map(unit => ({ value: unit.id, label: unit.name }))
                  ]}
                  value={importUnit}
                  onChange={setImportUnit}
                  placeholder="-- Chọn đơn vị --"
                />
              </div>
            )}

            <div className="form-group">
              <label>Chọn file Excel:</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files[0])}
              />
            </div>

            {importResult && (
              <div className="import-result">
                <h4>Kết quả import:</h4>
                <p>Tổng số: {importResult.summary.total}</p>
                <p className="success">Thành công: {importResult.summary.imported}</p>
                <p className="warning">Trùng lặp: {importResult.summary.duplicates}</p>
                <p className="error">Lỗi: {importResult.summary.errors}</p>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => {
                setShowImportModal(false);
                setImportFile(null);
                setImportResult(null);
                setImportUnit('');
              }}>
                Đóng
              </button>
              <button
                className="btn-primary"
                onClick={handleImport}
                disabled={!importFile || loading}
              >
                {loading ? 'Đang import...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedVehicle && (
        <VehicleDetailForm
          vehicle={selectedVehicle}
          onSave={handleSaveDetail}
          onReset={() => handleResetDetail(selectedVehicle.id)}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedVehicle(null);
          }}
        />
      )}
    </div>
  );
};

export default VehicleDetailManagement;
